import { useState } from 'react';

import {
  ChakraFlex,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  UniversalSelect,
} from '@equip.health/ui';
import { PatientClinicalEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  EQUIP_SCALE_REORDER_TEXT,
  OrderEquipScaleFrom,
} from '~/lib/constants/patients.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { EquipScaleWarning } from '~/lib/hooks/useEquipScaleWarnings';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import { useGetBodyTraceScaleReorderReasonsQuery } from '~/store/services/clinicalData';

import { PROCEED_WITH_ORDER_EVENT_BY_WARNING } from './equipScale.constants';
import WarningContent from './WarningContent';

const TOP_LEVEL_ID = 'reorder-equip-scale-modal';

interface ReorderEquipScaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: (reasonCode: string) => void;
  warnings?: EquipScaleWarning[];
}

const ReorderEquipScaleModal = ({
  isOpen,
  onClose,
  onContinue,
  warnings = [],
}: ReorderEquipScaleModalProps) => {
  const { trackEvent } = useAnalytics();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [currentWarning, setCurrentWarning] = useState<number>(0);

  const pendingWarning = warnings[currentWarning];

  const { data: reorderReasons = [] } = useGetBodyTraceScaleReorderReasonsQuery(
    undefined,
    { skip: !isOpen },
  );

  const reasonOptions = reorderReasons.map(({ code, label }) => ({
    label,
    value: code,
  }));

  const handleClose = () => {
    setSelectedReason('');
    setCurrentWarning(0);
    onClose();
  };

  const handleProceedWithWarning = () => {
    const event =
      PROCEED_WITH_ORDER_EVENT_BY_WARNING[OrderEquipScaleFrom.DEVICE][
        pendingWarning.type
      ];
    if (event) {
      trackEvent(event, {
        source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
      });
    }
    setCurrentWarning((previousWarning) => previousWarning + 1);
  };

  const handleContinue = () => {
    trackEvent(PatientClinicalEvents.equipScaleReorderContinueButtonClicked, {
      source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
    });
    onContinue(selectedReason);
    handleClose();
  };
  const handleCancel = () => {
    trackEvent(PatientClinicalEvents.equipScaleReorderCancelButtonClicked, {
      source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
    });
    handleClose();
  };

  return (
    <ModalV2
      id={`${TOP_LEVEL_ID}__modal`}
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title={pendingWarning ? pendingWarning.title : 'Reorder Equip scale'}
      footerContent={
        pendingWarning ? (
          <FireflyButton
            id={`${TOP_LEVEL_ID}__proceed-with-order-button`}
            onClick={handleProceedWithWarning}
          >
            Proceed with order
          </FireflyButton>
        ) : (
          <ChakraFlex justifyContent="space-between" width="100%">
            <FireflyButton
              id={`${TOP_LEVEL_ID}__cancel-button`}
              onClick={handleCancel}
              variant="secondary"
            >
              Cancel
            </FireflyButton>
            <FireflyButton
              id={`${TOP_LEVEL_ID}__continue-button`}
              isDisabled={!selectedReason}
              onClick={handleContinue}
              variant="primary"
            >
              Continue
            </FireflyButton>
          </ChakraFlex>
        )
      }
    >
      {pendingWarning ? (
        <WarningContent warning={pendingWarning.type} />
      ) : (
        <ChakraFlex direction="column" gap="12px">
          <Text color="neutral.primary" variant="body">
            {EQUIP_SCALE_REORDER_TEXT}
          </Text>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__reason-input-group`}
            isRequired
            label="Reason for replacing scale"
            shouldShowAsteriskWhenRequired
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__reason-select`}
              isSingleSelect
              modalId={`${TOP_LEVEL_ID}__modal`}
              onChange={(option?: { value?: string }) => {
                trackEvent(
                  PatientClinicalEvents.equipScaleReorderReasonOptionSelected,
                  {
                    source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
                  },
                );
                setSelectedReason(option?.value ?? '');
              }}
              onMenuOpen={() =>
                trackEvent(
                  PatientClinicalEvents.equipScaleReorderReasonDropdownClicked,
                  {
                    source: AnalyticsSourceValues.PATIENT_CLINICAL_PROFILE_TAB,
                  },
                )
              }
              options={reasonOptions}
              placeholder="Select one"
              value={getSelectedUniversalSelectOption(
                selectedReason,
                reasonOptions,
              )}
            />
          </InputGroupV2>
        </ChakraFlex>
      )}
    </ModalV2>
  );
};

export default ReorderEquipScaleModal;
